// Core API functionality
import { supabase } from '../supabase-client';

// Types
export type TradingBot = {
  id: string;
  name: string;
  description: string;
  risk_level: 'low' | 'medium' | 'high';
  pnl_percentage: number;
  running_days: number;
  webhook_url: string;
  is_active: boolean;
  show_on_public: boolean;
  settings?: any;
};

export type TradingPair = {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  is_active: boolean;
};

// Core API functions
export async function getBalance(exchange: string, apiKey: string, apiSecret: string): Promise<number> {
  try {
    const timestamp = Date.now().toString();
    const signature = await generateSignature(apiKey, apiSecret, timestamp);
    
    const response = await fetch(`https://api.bybit.com/v5/account/wallet-balance`, {
      headers: {
        'X-BAPI-API-KEY': apiKey,
        'X-BAPI-SIGN': signature,
        'X-BAPI-TIMESTAMP': timestamp,
        'X-BAPI-RECV-WINDOW': '5000'
      }
    });

    if (!response.ok) return 0;
    const data = await response.json();
    return data.result?.balance || 0;
  } catch (error) {
    console.error('Error getting balance:', error);
    return 0;
  }
}

export async function getPositions(exchange: string, apiKey: string, apiSecret: string): Promise<any[]> {
  try {
    const timestamp = Date.now().toString();
    const signature = await generateSignature(apiKey, apiSecret, timestamp);
    
    const response = await fetch(`https://api.bybit.com/v5/position/list`, {
      headers: {
        'X-BAPI-API-KEY': apiKey,
        'X-BAPI-SIGN': signature,
        'X-BAPI-TIMESTAMP': timestamp,
        'X-BAPI-RECV-WINDOW': '5000'
      }
    });

    if (!response.ok) return [];
    const data = await response.json();
    return data.result?.list || [];
  } catch (error) {
    console.error('Error getting positions:', error);
    return [];
  }
}

async function generateSignature(apiKey: string, apiSecret: string, timestamp: string): Promise<string> {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(apiSecret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(timestamp + apiKey)
  );

  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Database functions
export async function getBots() {
  const { data } = await supabase
    .from('trading_bots')
    .select('*')
    .order('created_at', { ascending: true });
  return data || [];
}

export async function getTradingPairs() {
  const { data } = await supabase
    .from('trading_pairs')
    .select('*')
    .eq('is_active', true)
    .order('symbol');
  return data || [];
}

export async function saveBotSettings(botId: string, settings: any) {
  return supabase
    .from('trading_bots')
    .update({ settings, updated_at: new Date().toISOString() })
    .eq('id', botId);
}

export async function startTrading(userId: string, botId: string) {
  return supabase
    .from('user_bot_status')
    .upsert({
      user_id: userId,
      bot_id: botId,
      is_trading: true,
      updated_at: new Date().toISOString()
    });
}

export async function stopTrading(userId: string, botId: string) {
  return supabase
    .from('user_bot_status')
    .update({
      is_trading: false,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .eq('bot_id', botId);
}
